import type { AxiosResponse, InternalAxiosRequestConfig } from "axios"
import { AxiosError } from "axios"

export class HttpException<T> extends AxiosError<ServerResponse<T>> {
  config: InternalAxiosRequestConfig<any> | undefined
  code?: string
  request?: any
  response?: AxiosResponse<ServerResponse<T>>
  status?: number | undefined

  constructor(error: AxiosError<ServerResponse<T>>) {
    let message = error.message

    if (error.response && error.response.data && error.response.data.Message) {
      message = error.response.data.Message
    }

    super(message)

    Object.setPrototypeOf(this, new.target.prototype)
    this.name = new.target.name

    this.config = error.config
    this.code = error.code
    this.request = error.request
    this.response = error.response
    this.status = error.status
  }

  get statusCode(): number | null {
    return this.response ? this.response.status : null
  }

  toJSON = () => ({
    message: this.message,
    name: this.name,
    stack: this.stack,
    config: this.config,
    code: this.code,
  })
}

export class ForbiddenException<T> extends HttpException<T> {}

export class UnauthorisedException<T> extends HttpException<T> {}

export class NotFoundException<T> extends HttpException<T> {}

export class ValidationException<T> extends HttpException<T> {
  get validationErrors(): { [key: string]: string[] } {
    if (this.response && this.response.data && this.response.data.Errors) {
      return this.response.data.Errors
    }

    return {}
  }

  firstValidationError(key?: string): string | null {
    if (!Object.keys(this.validationErrors).length)
      return null

    if (key) {
      return key in this.validationErrors
        ? this.validationErrors[key][0]
        : null
    }

    return this.validationErrors[Object.keys(this.validationErrors)[0]][0]
  }
}

export interface ServerResponseV1<T, U = MetaV1> {
  data?: T
  message?: string
  meta?: U
  links?: LinksV1
  errorCode?: string
  errors?: {
    [key: string]: string[]
  }
}

export interface ServerResponse<T, U = Record<string, any>> {
  Data?: T
  Message?: string
  Meta?: U
  Links?: Links
  Errors?: {
    [key: string]: string[]
  }
}

export interface ErrorServerResponse {
  Message: string
  Errors?: {
    [key: string]: string[]
  }
}

export interface Links {
  Current?: string
  First?: string
  Last?: string
  Prev?: string
  Next?: string
}

export interface LinksV1 {
  self?: string
  first?: string
  prev?: string
  next?: string
  last?: string
}

export interface MetaV1<T = Record<string, unknown | undefined>> {
  currentPage?: number
  totalPages?: number
  pageSize?: number
  totalCount?: number
  hasNext?: boolean
  hasPrevious?: boolean
  extensions?: T
}

export function handleHttpException<T>(error: AxiosError<ServerResponse<T>>) {
  switch (error.response?.status) {
    case 401:
      throw new UnauthorisedException(error)
    case 403:
      throw new ForbiddenException(error)
    case 404:
      throw new NotFoundException(error)
    case 422:
      throw new ValidationException(error)
  }

  throw error
}

export const ERROR_CODES = [
  "ERROR_UPLOAD_LIMIT_EXCEEDED",
  "PROVIDER_NOT_AVAILABLE",
  "REMOTE_ERROR",
  "INFO_MISSING",
  "EMAIL_NOT_RECEIVED",
  "USER_CREATION_FAILED",
  "ACCOUNT_LOCKED",
  "NO_ACCOUNT_FOUND",
  "EMAIL_MISMATCH",
  "ERROR_UNSUPPORTED_DOCUMENT_TYPE",
  "ERROR_DOCUMENT_SIZE_LIMIT_EXCEEDED",
] as const

export type ErrorCode = (typeof ERROR_CODES)[number]

export function isErrorCode(code: unknown): code is ErrorCode {
  return typeof code === "string" && ERROR_CODES.includes(code as ErrorCode)
}

export function getErrorCode(data: unknown): ErrorCode | undefined {
  if (typeof data !== "object" || data === null) {
    return undefined
  }

  const errorCode = (data as { errorCode?: unknown }).errorCode

  return isErrorCode(errorCode) ? errorCode : undefined
}
